import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Input from 'components/inputs/input/Input';
import CreateNewProfilesEntitiesListRowTextArea from './components/CreateNewProfilesEntitiesListRowTextArea';

import {
  deleteEntity,
  updateEntity
} from 'modules/create-new-profiles/reducers/actions';

import { __t, applyTranslations } from 'utils/language';

import './create-new-profiles-entities-list-row.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./create-new-profiles-entities-list-row.i18n.json'));

import { CreateNewProfilesEntity } from 'modules/create-new-profiles/types/CreateNewProfiles.type';
import {
  LightweighInfluencerCountry,
  LightweighInfluencerGender
} from 'modules/create-new-profiles/types/general';
import { MixedSelectDropdownItem, State } from 'types';

interface OwnProps {
  entity: CreateNewProfilesEntity,
  index: number,
  isDeletable: boolean
}

interface StateProps {
  countries: Array<MixedSelectDropdownItem>
}

interface DispatchProps {
  dispatchOnArtistNameChange: (n: string | null, i: number) => void,
  dispatchOnChannelURLChange: (t: Array<string>, i: number) => void,
  dispatchOnGenderChange: (g: LightweighInfluencerGender | null, i: number) => void,
  dispatchOnLocationsChange: (l: Array<LightweighInfluencerCountry>, i: number) => void,
  dispatchOnEmailChange: (e: string | null, i: number) => void,
  dispatchOnDelete: (i: number) => void
}

type Props = OwnProps & StateProps & DispatchProps;

const COMMON_MSG_KEY = 'collabary_common';

const CreateNewProfilesEntitiesListRow = ({
  countries,
  entity,
  index,
  isDeletable,
  dispatchOnArtistNameChange,
  dispatchOnChannelURLChange,
  dispatchOnLocationsChange: dispatchOnCountriesChange,
  dispatchOnDelete,
  dispatchOnEmailChange,
  dispatchOnGenderChange
}: Props): JSX.Element => {
  const artistNameClasses = classnames(
    'create-new-profiles-entities-list-row__cell-container',
    'create-new-profiles-entities-list-row__cell-container--artist-name'
  );
  const artistNameErrorClasses = classnames({
    'create-new-profiles-entities-list-row__artist-name-input': true,
    'create-new-profiles-entities-list-row__artist-name-input--has-error':
      entity.invalidFields && entity.invalidFields.includes('name')
  });
  const channelURLClasses = classnames(
    'create-new-profiles-entities-list-row__cell-container',
    'create-new-profiles-entities-list-row__cell-container--channel-url'
  );
  const channelURLErrorClasses = classnames({
    'create-new-profiles-entities-list-row__channel-urls-input': true,
    'create-new-profiles-entities-list-row__channel-urls-input--has-error':
      entity.invalidFields && (
        entity.invalidFields.includes('urls') || entity.invalidFields.includes('separator')
      )
  });
  const genderClasses = classnames(
    'create-new-profiles-entities-list-row__cell-container',
    'create-new-profiles-entities-list-row__cell-container--gender'
  );
  const countryClasses = classnames(
    'create-new-profiles-entities-list-row__cell-container',
    'create-new-profiles-entities-list-row__cell-container--country'
  );
  const emailClasses = classnames(
    'create-new-profiles-entities-list-row__cell-container',
    'create-new-profiles-entities-list-row__cell-container--email'
  );
  const emailErrorClasses = classnames({
    'create-new-profiles-entities-list-row__email-input': true,
    'create-new-profiles-entities-list-row__email-input--has-error':
      entity.invalidFields && entity.invalidFields.includes('email')
  });
  const deleteClasses = classnames(
    'create-new-profiles-entities-list-row__cell-container',
    'create-new-profiles-entities-list-row__cell-container--delete'
  );

  return (
    <div className="create-new-profiles-entities-list-row">
      <div className={artistNameClasses}>
        <Input
          width="responsive"
          value={entity.artistName ?? ''}
          placeholder={__t('create_new_profiles_entities_list_row.placeholders.artist_name')}
          className={artistNameErrorClasses}
          onChange={event => dispatchOnArtistNameChange(event.currentTarget.value, index)}
        />
      </div>
      <div className={channelURLClasses}>
        <CreateNewProfilesEntitiesListRowTextArea
          placeholder={__t('create_new_profiles_entities_list_row.placeholders.urls')}
          inputClassName={channelURLErrorClasses}
          channels={entity.channelUrls.join(',\n')}
          onChange={text => dispatchOnChannelURLChange(
            text.split(',').map(v => v.trim()),
            index
          )}
        />
      </div>
      <div className={genderClasses}>
        <MixedSelectDropdown
          items={['F', 'M', 'G', 'N'].map(value => ({
            identifier: value,
            isDisabled: false,
            isSelected: entity.gender === value,
            label: __t(`${COMMON_MSG_KEY}.gender.${value.toLowerCase()}`),
            value
          }))}
          placeholder={__t('create_new_profiles_entities_list_row.placeholders.gender')}
          name="gender"
          withRemovable
          onItemsToggle={(_, __, item) => {
            const gender = item?.identifier as LightweighInfluencerGender;

            dispatchOnGenderChange(gender, index);
          }}
          onItemsRemoveAll={() => dispatchOnGenderChange(null, index)}

        />
      </div>
      <div className={countryClasses}>
        <MixedSelectDropdown
          items={countries.map(c => ({
            ...c,
            isSelected: entity.locations.filter(ctr => ctr.country === c.value).length > 0
          }))}
          placeholder={__t('create_new_profiles_entities_list_row.placeholders.countries')}
          name="countries"
          withRemovable
          withSearchable
          withMulti
          withTruncate
          onItemsToggle={(_, __, item) => dispatchOnCountriesChange(
            [
              ...entity.locations,
              {
                country: item?.value ?? item?.label ?? '',
                countryCode: item?.identifier ?? ''
              }
            ],
            index
          )}
          onItemsRemoveAll={() => dispatchOnCountriesChange(
            [],
            index
          )}
          onItemsRemove={(_, __, item) => dispatchOnCountriesChange(
            entity.locations.filter(c => c.countryCode !== item?.identifier),
            index
          )}
        />
      </div>
      <div className={emailClasses}>
        <Input
          width="responsive"
          value={entity.email ?? ''}
          placeholder={__t('create_new_profiles_entities_list_row.placeholders.email')}
          className={emailErrorClasses}
          onChange={event => dispatchOnEmailChange(event.currentTarget.value, index)}
        />
      </div>
      <div className={deleteClasses}>
        <SVGDynamicIcon
          name="trash-custom-fg"
          width={12}
          height={12}
          customColor="grey"
          onClick={() => dispatchOnDelete(index)}
          disabled={!isDeletable}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ createNewProfiles }: State): StateProps => ({
  countries: createNewProfiles.countries
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchOnArtistNameChange: (name, index) => dispatch(updateEntity(
    { type: 'artist-name', name, index }
  )),
  dispatchOnChannelURLChange: (urls, index) => dispatch(updateEntity(
    { type: 'channel-urls', urls, index }
  )),
  dispatchOnLocationsChange: (locations, index) => dispatch(updateEntity(
    { type: 'locations', locations, index }
  )),
  dispatchOnEmailChange: (email, index) => dispatch(updateEntity(
    { type: 'email', email, index }
  )),
  dispatchOnGenderChange: (gender, index) => dispatch(updateEntity(
    { type: 'gender', gender, index }
  )),
  dispatchOnDelete: (index) => dispatch(deleteEntity(index))
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateNewProfilesEntitiesListRow);
